// Subtle "Up next: <title>" pill shown during the final ~5s of the active
// track. Fades in/out via opacity + translate transition so it never jars the
// listener. Driven entirely by `upNextTrack` from PlayerContext — no local
// timers or duration math, all that lives in the player.

import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

export default function UpNextPreview({ className = "", testIdPrefix = "up-next" }) {
  const { upNextTrack } = usePlayer();
  // Keep the last seen track around for a moment after `upNextTrack` clears so
  // the fade-OUT can play the text before unmounting (otherwise the title
  // would disappear instantly while the opacity is still animating).
  const [shown, setShown] = useState(null);
  const clearTimerRef = useRef(null);

  useEffect(() => {
    if (upNextTrack) {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setShown(upNextTrack);
      return;
    }
    // upNextTrack went null → start fade-out, then clear `shown` after the
    // CSS transition wraps so the DOM unmounts cleanly.
    if (shown) {
      clearTimerRef.current = setTimeout(() => setShown(null), 450);
    }
    return () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, [upNextTrack, shown]);

  if (!shown) return null;
  const visible = Boolean(upNextTrack);

  return (
    <div
      data-testid={`${testIdPrefix}-pill`}
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full border border-[#C5A059]/25 bg-black/40 backdrop-blur-sm text-[11px] tracking-widest uppercase text-[#A1A4AB] transition-all duration-400 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"} ${className}`}
      style={{ transitionDuration: "400ms" }}
    >
      <ChevronRight className="w-3 h-3 text-[#C5A059]" strokeWidth={2} />
      <span className="text-[#C5A059] font-medium">Up next</span>
      <span className="text-[#A1A4AB] normal-case tracking-normal truncate max-w-[240px] md:max-w-[360px]">
        {shown.title}
        {shown.artist ? <span className="text-[#6e7079]"> · {shown.artist}</span> : null}
      </span>
    </div>
  );
}
